import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, Save } from 'lucide-react';
import { sanitizeWorkspaceSlug, setupOrAdminAuthHeaders } from '../utils/workspace';

interface SetupPromptReviewPageProps {
  isDarkMode: boolean;
}

interface BehaviorDraftResponse {
  customerSlug?: string;
  workspaceSlug?: string;
  agentSlug?: string;
  agentName?: string;
  source?: 'saved' | 'draft';
  modifierText?: string;
  draftText?: string;
  suggestedText?: string;
  updatedAt?: string | null;
  profileUpdatedAt?: string | null;
  profileMissing?: boolean;
  profile_missing?: boolean;
  behaviorMode?: 'blueprint' | 'custom';
  activeSource?: 'blueprint' | 'custom';
  hasExistingPrompt?: boolean;
  existingPromptChars?: number;
  requiresWriteConfirmation?: boolean;
  protectedSetup?: boolean;
  error?: string;
  code?: string;
}

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocalhost ? 'http://localhost:5000' : '';

const SetupPromptReviewPage: React.FC<SetupPromptReviewPageProps> = ({ isDarkMode }) => {
  const navigate = useNavigate();
  const {
    customerSlug: customerSlugParam,
    workspaceSlug: workspaceSlugParam,
    agentSlug: agentSlugParam,
  } = useParams<{ customerSlug: string; workspaceSlug: string; agentSlug: string }>();
  const customerSlug = sanitizeWorkspaceSlug(customerSlugParam);
  const workspaceSlug = sanitizeWorkspaceSlug(workspaceSlugParam || 'main');
  const agentSlug = sanitizeWorkspaceSlug(agentSlugParam || 'sally');
  const [draft, setDraft] = useState('');
  const [suggestedDraft, setSuggestedDraft] = useState('');
  const [source, setSource] = useState<'saved' | 'draft'>('draft');
  const [activeSource, setActiveSource] = useState<'blueprint' | 'custom'>('blueprint');
  const [agentName, setAgentName] = useState(agentSlug);
  const [hasExistingPrompt, setHasExistingPrompt] = useState(false);
  const [requiresWriteConfirmation, setRequiresWriteConfirmation] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [protectedSetup, setProtectedSetup] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [profileMissing, setProfileMissing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadDraft = async () => {
      setIsLoading(true);
      setError('');
      setSaveMessage('');
      setProfileMissing(false);

      try {
        const response = await fetch(
          `${API_BASE}/api/customer/workspace/${encodeURIComponent(customerSlug)}`
          + `/workspaces/${encodeURIComponent(workspaceSlug)}`
          + `/agents/${encodeURIComponent(agentSlug)}/behavior-draft`,
          { headers: setupOrAdminAuthHeaders(customerSlug) },
        );
        const payload = (await response.json().catch(() => null)) as BehaviorDraftResponse | null;
        if (!response.ok) {
          throw new Error(payload && typeof payload === 'object' && 'error' in payload ? String(payload.error) : 'draft_failed');
        }

        if (cancelled) {
          return;
        }

        const loadedDraft = payload?.modifierText || payload?.draftText || '';
        setDraft(loadedDraft);
        setSuggestedDraft(payload?.suggestedText || payload?.draftText || '');
        setSource(payload?.source === 'saved' ? 'saved' : 'draft');
        setActiveSource(payload?.activeSource === 'custom' ? 'custom' : 'blueprint');
        setAgentName(payload?.agentName || agentSlug);
        setHasExistingPrompt(Boolean(payload?.hasExistingPrompt));
        setRequiresWriteConfirmation(Boolean(payload?.requiresWriteConfirmation));
        setProtectedSetup(Boolean(payload?.protectedSetup));
        setConfirmOverwrite(false);
        setUpdatedAt(payload?.updatedAt ?? null);
        setProfileMissing(Boolean(payload?.profileMissing || payload?.profile_missing));
      } catch {
        if (!cancelled) {
          setError('Prompt-Vorschlag konnte nicht geladen werden.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadDraft();

    return () => {
      cancelled = true;
    };
  }, [agentSlug, customerSlug, workspaceSlug]);

  const pageClasses = isDarkMode
    ? 'bg-slate-950 text-slate-50'
    : 'bg-slate-100 text-slate-950';
  const panelClasses = isDarkMode
    ? 'border-white/10 bg-slate-900 text-slate-100'
    : 'border-slate-200 bg-white text-slate-950';
  const inputClasses = isDarkMode
    ? 'border-slate-700 bg-slate-950 text-slate-100 focus:border-blue-400'
    : 'border-slate-300 bg-white text-slate-950 focus:border-blue-500';
  const mutedClasses = isDarkMode ? 'text-slate-300' : 'text-slate-500';

  const statusText = useMemo(() => {
    if (source === 'saved') {
      return updatedAt ? `Gespeichert: ${updatedAt}` : 'Gespeichert';
    }
    if (profileMissing) {
      return 'Basis-Entwurf ohne Website-Profil';
    }
    return 'Vorschlag aus dem Website-Profil';
  }, [profileMissing, source, updatedAt]);

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSaveMessage('');

    try {
      const response = await fetch(`${API_BASE}/api/customer/workspace/${encodeURIComponent(customerSlug)}`
        + `/workspaces/${encodeURIComponent(workspaceSlug)}`
        + `/agents/${encodeURIComponent(agentSlug)}/behavior`, {
        method: 'PUT',
        headers: setupOrAdminAuthHeaders(customerSlug),
        body: JSON.stringify({
          modifier_text: draft,
          confirmExistingPromptOverwrite: confirmOverwrite,
          confirmProtectedCustomerOverwrite: confirmOverwrite,
        }),
      });
      const payload = (await response.json().catch(() => null)) as BehaviorDraftResponse | null;
      if (!response.ok) {
        throw new Error(payload?.code || payload?.error || 'save_failed');
      }

      setSource('saved');
      setActiveSource('custom');
      setHasExistingPrompt(true);
      setRequiresWriteConfirmation(true);
      setConfirmOverwrite(false);
      setUpdatedAt(payload?.updatedAt ?? null);
      setSaveMessage(`Eigener Behaviour Prompt für ${agentName} ist jetzt aktiv.`);
    } catch (saveError) {
      setError(saveError instanceof Error && saveError.message === 'existing_prompt_requires_confirmation'
        ? 'Der bestehende Prompt ist geschützt. Bitte bestätige das Überschreiben ausdrücklich.'
        : 'Prompt konnte nicht gespeichert werden.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main className={`min-h-screen px-4 py-6 sm:px-6 lg:px-8 ${pageClasses}`}>
      <div className="mx-auto max-w-5xl space-y-5">
        <header className="flex flex-col gap-4 border-b border-slate-200/70 pb-5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate(`/dashboard/${customerSlug}`)}
              className={`mb-4 inline-flex items-center gap-2 text-sm font-semibold ${mutedClasses}`}
            >
              <ArrowLeft className="h-4 w-4" />
              Dashboard
            </button>
            <h1 className="text-2xl font-semibold sm:text-3xl">Behaviour Prompt für {agentName}</h1>
            <p className={`mt-1 text-sm ${mutedClasses}`}>
              Scope: {customerSlug} / {workspaceSlug} / {agentSlug}
            </p>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || isLoading || protectedSetup || (requiresWriteConfirmation && !confirmOverwrite)}
            className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition ${
              isSaving || isLoading || protectedSetup || (requiresWriteConfirmation && !confirmOverwrite)
                ? 'cursor-not-allowed bg-slate-300'
                : 'bg-slate-950 hover:bg-slate-800'
            }`}
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Speichern & eigenen Prompt aktivieren
          </button>
        </header>

        <section className={`rounded-lg border p-5 ${panelClasses}`}>
          <div className={`mb-4 rounded-lg border px-4 py-3 text-sm font-semibold ${
            activeSource === 'custom'
              ? isDarkMode ? 'border-emerald-400/35 bg-emerald-950/30 text-emerald-100' : 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : isDarkMode ? 'border-amber-400/35 bg-amber-950/30 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}>
            Aktiv: {activeSource === 'custom'
              ? `eigener Behaviour Prompt für ${agentName}`
              : 'geerbter Kunden-Blueprint'}
          </div>

          {protectedSetup ? (
            <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
              Der Setup Wizard ist für Synaworks / Sally gesperrt. Der bestehende Prompt kann hier nicht verändert werden.
            </div>
          ) : null}

          <div className={`mb-4 rounded-lg border px-4 py-3 text-sm ${isDarkMode ? 'border-white/10 bg-slate-950/60' : 'border-slate-200 bg-slate-50'}`}>
            <p className="font-semibold">Gemeinsame Fakten, eigenes Verhalten</p>
            <p className={`mt-1 ${mutedClasses}`}>
              Der Vorschlag nutzt das gemeinsame Website-Profil als Faktenbasis. Gespeichert wird ausschließlich der Behaviour Prompt von {agentName}; Core Prompt und andere Avatare bleiben unverändert.
            </p>
            {suggestedDraft && suggestedDraft !== draft ? (
              <button
                type="button"
                onClick={() => setDraft(suggestedDraft)}
                className="mt-3 rounded border border-blue-300 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
              >
                Avatar-spezifischen Vorschlag aus Website-Profil übernehmen
              </button>
            ) : null}
          </div>
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className={`h-4 w-4 ${source === 'saved' ? 'text-emerald-500' : 'text-blue-500'}`} />
              <span className={`text-sm ${mutedClasses}`}>{statusText}</span>
            </div>
            {saveMessage ? <span className="text-sm text-emerald-600">{saveMessage}</span> : null}
          </div>

          {isLoading ? (
            <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-sm ${isDarkMode ? 'border-white/10 bg-slate-950/60' : 'border-slate-200 bg-slate-50'} ${mutedClasses}`}>
              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
              Prompt wird geladen...
            </div>
          ) : (
            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              className={`min-h-[520px] w-full resize-y rounded-lg border p-4 font-mono text-sm leading-6 outline-none transition ${inputClasses}`}
              spellCheck={false}
            />
          )}

          {error ? <p className="mt-3 text-sm text-red-500">{error}</p> : null}

          {requiresWriteConfirmation && !protectedSetup ? (
            <label className={`mt-4 flex items-start gap-3 rounded-lg border p-3 text-sm ${isDarkMode ? 'border-amber-400/30 bg-amber-950/20' : 'border-amber-200 bg-amber-50'}`}>
              <input
                type="checkbox"
                checked={confirmOverwrite}
                onChange={(event) => setConfirmOverwrite(event.target.checked)}
                className="mt-0.5"
              />
              <span>
                {hasExistingPrompt
                  ? `Ich bestätige, dass der bestehende Behaviour Prompt von ${agentName} überschrieben werden darf.`
                  : `Ich bestätige, dass für den geschützten Kunden ${customerSlug} ein neuer Behaviour Prompt für ${agentName} gespeichert werden darf.`}
                {' '}Core Prompt, Knowledgebase und andere Avatare werden nicht verändert.
              </span>
            </label>
          ) : null}
        </section>
      </div>
    </main>
  );
};

export default SetupPromptReviewPage;
